"use client";

import { useState, useEffect, useCallback } from "react";
import { METRICS_EVENTS } from "@cede/types";
import { CompletedBadge } from "./CompletedBadge";
import { CRYPTOCOM_SUBMIT_COOLDOWN_SECONDS } from "./constants";
import { Button } from "@/components/ui/button";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useSubmitCryptoComRefereeRequest } from "@/hooks/data/useSubmitCryptoComRefereeRequest";
import { useHubUser } from "@/hooks/user/useHubUser";
import { Check } from "lucide-react";
import { useSnackbar } from "notistack";

type SubmitForReviewProps = {
  walletConnected: boolean;
  oauthConnected: boolean;
  cryptoComAccountId: string | null;
  submitted: boolean;
  onSubmitted?: () => void;
};

export const SubmitForReview = ({
  walletConnected,
  oauthConnected,
  cryptoComAccountId,
  submitted,
  onSubmitted,
}: SubmitForReviewProps) => {
  const { trackEvent } = useMetricsEvents();
  const { hubUser } = useHubUser();
  const submitRequest = useSubmitCryptoComRefereeRequest();
  const { enqueueSnackbar } = useSnackbar();
  const [cooldown, setCooldown] = useState(0);

  const canSubmit = walletConnected && oauthConnected && cooldown === 0 && !submitted;

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleSubmit = useCallback(async () => {
    if (!hubUser?.defiAddress || !cryptoComAccountId) return;

    try {
      const correlationId = crypto.randomUUID();
      await submitRequest.mutateAsync({
        exchangeAccountId: cryptoComAccountId,
        correlationId,
      });

      onSubmitted?.();
      enqueueSnackbar("Submission sent successfully!", { variant: "success" });

      trackEvent(METRICS_EVENTS.CRYPTO_COM_REVIEW_SUBMITTED, {
        wallet_address: hubUser.defiAddress,
        exchange_account_id: cryptoComAccountId,
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Submission failed. Please try again.";
      enqueueSnackbar(errorMessage, { variant: "error" });
      setCooldown(CRYPTOCOM_SUBMIT_COOLDOWN_SECONDS);
    }
  }, [hubUser, cryptoComAccountId, submitRequest, trackEvent, enqueueSnackbar, onSubmitted]);

  const submitLabel = submitRequest.isPending ? "Submitting..." : cooldown > 0 ? `Wait ${cooldown}s` : "Submit";

  if (submitted) {
    return <CompletedBadge label="Submitted" />;
  }

  return (
    <Button
      variant="main"
      size="small"
      leftIcon={<Check className="h-3.5 w-3.5" />}
      loading={submitRequest.isPending}
      disabled={!canSubmit}
      onClick={handleSubmit}
    >
      {submitLabel}
    </Button>
  );
};
